/**
 * TerraMatrix Universal Data Platform - DataSourceWizard
 *
 * Multi-step wizard for adding new data sources with AI-assisted descriptions.
 * Flow: Connection Details → Test & Discover → AI Description → Review & Save
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Database, Loader2, CheckCircle, XCircle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { dataApi } from '@/services/api/dataApi';
import { useToast } from '@/hooks/use-toast';

type WizardStep = 'connection' | 'discover' | 'description' | 'review';

interface ConnectionConfig {
  type: 'mongodb' | 'mysql' | 'postgresql';
  uri: string;
  database: string;
  name: string;
}

interface DiscoveryResult {
  collections: Array<{
    name: string;
    documentCount: number;
    purpose?: string;
    fields?: Record<string, any>;
  }>;
  summary: {
    totalCollections: number;
    totalDocuments: number;
  };
}

export const DataSourceWizard: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  // Wizard state
  const [currentStep, setCurrentStep] = useState<WizardStep>('connection');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form data
  const [config, setConfig] = useState<ConnectionConfig>({
    type: 'mongodb',
    uri: '',
    database: '',
    name: '',
  });

  // Discovery results
  const [discoveryResult, setDiscoveryResult] = useState<DiscoveryResult | null>(null);

  // AI-generated description
  const [aiDescription, setAiDescription] = useState('');
  const [aiTags, setAiTags] = useState<string[]>([]);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);

  // User-edited description
  const [userDescription, setUserDescription] = useState('');
  const [userTags, setUserTags] = useState('');

  // Step 1: Test connection and discover schema
  // Note: Schema discovery feature temporarily unavailable (analytics-backend removed)
  const handleTestConnection = useCallback(async () => {
    if (!config.uri || !config.database) {
      setError('Please enter both connection URI and database name');
      return;
    }

    // Schema discovery is temporarily unavailable
    setError('Schema discovery is temporarily unavailable. Please use the Data page to query existing data sources.');
    toast({
      title: 'Feature Unavailable',
      description: 'Adding new data sources is temporarily disabled.',
      variant: 'destructive',
    });
  }, [config, toast]);

  // Step 2: Generate AI description based on discovered schema
  const handleGenerateDescription = useCallback(async () => {
    if (!discoveryResult) return;

    setIsGeneratingDescription(true);

    try {
      // Build context for AI description generation
      const collectionNames = discoveryResult.collections.map(c => c.name);
      const collectionPurposes = discoveryResult.collections
        .filter(c => c.purpose)
        .map(c => `${c.name}: ${c.purpose}`);

      // Call AI endpoint to generate description
      const response = await fetch('/api/analytics/ai/describe-source', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          databaseName: config.database,
          collections: collectionNames,
          collectionPurposes,
          totalDocuments: discoveryResult.summary.totalDocuments,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setAiDescription(data.description || '');
        setAiTags(data.tags || []);
        setUserDescription(data.description || '');
        setUserTags((data.tags || []).join(', '));
      } else {
        // Fallback: Generate basic description locally
        const description = generateLocalDescription(config.database, discoveryResult);
        setAiDescription(description);
        setUserDescription(description);
        setUserTags(generateLocalTags(discoveryResult.collections));
      }

      setCurrentStep('description');
    } catch (err) {
      // Fallback to local generation on error
      const description = generateLocalDescription(config.database, discoveryResult);
      setAiDescription(description);
      setUserDescription(description);
      setUserTags(generateLocalTags(discoveryResult.collections));
      setCurrentStep('description');
    } finally {
      setIsGeneratingDescription(false);
    }
  }, [config.database, discoveryResult]);

  // Local fallback for description generation
  const generateLocalDescription = (dbName: string, result: DiscoveryResult): string => {
    const collectionCount = result.summary.totalCollections;
    const docCount = result.summary.totalDocuments;
    const collectionNames = result.collections.slice(0, 5).map(c => c.name).join(', ');

    return `Database containing ${collectionCount} collections with ${docCount.toLocaleString()} total documents. Collections include: ${collectionNames}${result.collections.length > 5 ? '...' : ''}.`;
  };

  const generateLocalTags = (collections: DiscoveryResult['collections']): string => {
    const tags = new Set<string>();
    collections.forEach(c => {
      const name = c.name.toLowerCase();
      if (name.includes('moisture') || name.includes('water')) tags.add('water');
      if (name.includes('sensor') || name.includes('device')) tags.add('sensors');
      if (name.includes('history') || name.includes('log')) tags.add('historical');
      if (name.includes('level')) tags.add('monitoring');
    });
    return Array.from(tags).join(', ');
  };

  // Step 3: Save the data source
  // Note: Using dataApi from data-service
  const handleSave = useCallback(async () => {
    if (!config.name) {
      setError('Please enter a name for the data source');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await dataApi.createSource({
        name: config.name,
        type: config.type,
        connectionConfig: {
          uri: config.uri,
          database: config.database,
        },
        metadata: {
          description: userDescription,
          tags: userTags.split(',').map(t => t.trim()).filter(Boolean),
        },
      });

      if (result) {
        toast({
          title: 'Data Source Created',
          description: `"${config.name}" has been added successfully.`,
        });
        // Navigate to the data source detail page
        navigate(`/data/sources/${result.dataSourceId}`);
      } else {
        setError('Failed to create data source');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save data source');
    } finally {
      setIsLoading(false);
    }
  }, [config, userDescription, userTags, navigate, toast]);

  // Render step indicator
  const renderStepIndicator = () => {
    const steps = [
      { key: 'connection', label: 'Connect' },
      { key: 'discover', label: 'Discover' },
      { key: 'description', label: 'Describe' },
      { key: 'review', label: 'Save' },
    ];

    const currentIndex = steps.findIndex(s => s.key === currentStep);

    return (
      <div className="flex items-center justify-center mb-8">
        {steps.map((step, index) => (
          <React.Fragment key={step.key}>
            <div className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  index <= currentIndex
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {index < currentIndex ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  index + 1
                )}
              </div>
              <span
                className={`ml-2 text-sm ${
                  index <= currentIndex ? 'text-foreground' : 'text-muted-foreground'
                }`}
              >
                {step.label}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div
                className={`w-16 h-0.5 mx-4 ${
                  index < currentIndex ? 'bg-primary' : 'bg-muted'
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  // Render connection step
  const renderConnectionStep = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="w-5 h-5" />
          Connect to Data Source
        </CardTitle>
        <CardDescription>
          Enter your database connection details. We'll test the connection and discover the schema.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="type">Database Type</Label>
          <Select
            value={config.type}
            onValueChange={(value) => setConfig({ ...config, type: value as ConnectionConfig['type'] })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mongodb">MongoDB</SelectItem>
              <SelectItem value="mysql">MySQL</SelectItem>
              <SelectItem value="postgresql">PostgreSQL</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="uri">Connection URI</Label>
          <Input
            id="uri"
            placeholder="mongodb://localhost:27017"
            value={config.uri}
            onChange={(e) => setConfig({ ...config, uri: e.target.value })}
          />
          <p className="text-xs text-muted-foreground">
            Full connection string including host, port, and credentials if needed.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="database">Database Name</Label>
          <Input
            id="database"
            placeholder="my_database"
            value={config.database}
            onChange={(e) => setConfig({ ...config, database: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="name">Data Source Name</Label>
          <Input
            id="name"
            placeholder="My Water Sensors"
            value={config.name}
            onChange={(e) => setConfig({ ...config, name: e.target.value })}
          />
          <p className="text-xs text-muted-foreground">
            A friendly name to identify this data source.
          </p>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg">
            <XCircle className="w-4 h-4" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <div className="flex justify-end pt-4">
          <Button onClick={handleTestConnection} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Testing Connection...
              </>
            ) : (
              <>
                Test & Discover
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  // Render discovery step
  const renderDiscoverStep = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-500" />
          Schema Discovered
        </CardTitle>
        <CardDescription>
          We found {discoveryResult?.summary.totalCollections} collections with{' '}
          {discoveryResult?.summary.totalDocuments.toLocaleString()} documents.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3 mb-6">
          {discoveryResult?.collections.slice(0, 8).map((collection) => (
            <div
              key={collection.name}
              className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
            >
              <div>
                <span className="font-medium">{collection.name}</span>
                {collection.purpose && (
                  <p className="text-xs text-muted-foreground">{collection.purpose}</p>
                )}
              </div>
              <span className="text-sm text-muted-foreground">
                {collection.documentCount.toLocaleString()} docs
              </span>
            </div>
          ))}
          {(discoveryResult?.collections.length || 0) > 8 && (
            <p className="text-sm text-muted-foreground text-center">
              +{(discoveryResult?.collections.length || 0) - 8} more collections
            </p>
          )}
        </div>

        <div className="flex justify-between pt-4 border-t">
          <Button variant="outline" onClick={() => setCurrentStep('connection')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button onClick={handleGenerateDescription} disabled={isGeneratingDescription}>
            {isGeneratingDescription ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating Description...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Generate AI Description
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  // Render description step
  const renderDescriptionStep = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-500" />
          AI-Generated Description
        </CardTitle>
        <CardDescription>
          Review and edit the AI-generated description. Good descriptions help the AI understand
          what queries this data can answer.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {aiDescription && (
          <div className="p-4 bg-purple-50 dark:bg-purple-950/30 rounded-lg border border-purple-200 dark:border-purple-800">
            <p className="text-sm text-purple-700 dark:text-purple-300 mb-2 font-medium">
              AI Suggestion:
            </p>
            <p className="text-sm">{aiDescription}</p>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            rows={4}
            placeholder="Describe what this data source contains and what questions it can answer..."
            value={userDescription}
            onChange={(e) => setUserDescription(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Describe the purpose and contents. E.g., "Soil moisture monitoring for cherry orchards.
            Tracks moisture levels by zone to optimize irrigation."
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="tags">Tags</Label>
          <Input
            id="tags"
            placeholder="agriculture, sensors, moisture, water"
            value={userTags}
            onChange={(e) => setUserTags(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Comma-separated keywords to help find this data source.
          </p>
        </div>

        <div className="flex justify-between pt-4 border-t">
          <Button variant="outline" onClick={() => setCurrentStep('discover')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button onClick={() => setCurrentStep('review')}>
            Review & Save
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  // Render review step
  const renderReviewStep = () => (
    <Card>
      <CardHeader>
        <CardTitle>Review & Save</CardTitle>
        <CardDescription>
          Confirm the data source configuration before saving.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          <div className="flex justify-between py-2 border-b">
            <span className="text-muted-foreground">Name</span>
            <span className="font-medium">{config.name || 'Unnamed'}</span>
          </div>
          <div className="flex justify-between py-2 border-b">
            <span className="text-muted-foreground">Type</span>
            <span className="font-medium capitalize">{config.type}</span>
          </div>
          <div className="flex justify-between py-2 border-b">
            <span className="text-muted-foreground">Database</span>
            <span className="font-medium">{config.database}</span>
          </div>
          <div className="flex justify-between py-2 border-b">
            <span className="text-muted-foreground">Collections</span>
            <span className="font-medium">{discoveryResult?.summary.totalCollections}</span>
          </div>
          <div className="flex justify-between py-2 border-b">
            <span className="text-muted-foreground">Documents</span>
            <span className="font-medium">
              {discoveryResult?.summary.totalDocuments.toLocaleString()}
            </span>
          </div>
        </div>

        <div className="p-3 bg-muted rounded-lg">
          <p className="text-sm font-medium mb-1">Description</p>
          <p className="text-sm text-muted-foreground">{userDescription || 'No description'}</p>
        </div>

        <div className="p-3 bg-muted rounded-lg">
          <p className="text-sm font-medium mb-1">Tags</p>
          <div className="flex flex-wrap gap-1">
            {userTags
              .split(',')
              .map((t) => t.trim())
              .filter(Boolean)
              .map((tag) => (
                <span
                  key={tag}
                  className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full"
                >
                  {tag}
                </span>
              ))}
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg">
            <XCircle className="w-4 h-4" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <div className="flex justify-between pt-4 border-t">
          <Button variant="outline" onClick={() => setCurrentStep('description')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Create Data Source
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="mb-8">
          <Button variant="ghost" onClick={() => navigate('/data')} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Data
          </Button>
          <h1 className="text-2xl font-bold">Add Data Source</h1>
          <p className="text-muted-foreground">
            Connect a database and let AI help describe your data.
          </p>
        </div>

        {/* Step Indicator */}
        {renderStepIndicator()}

        {/* Step Content */}
        {currentStep === 'connection' && renderConnectionStep()}
        {currentStep === 'discover' && renderDiscoverStep()}
        {currentStep === 'description' && renderDescriptionStep()}
        {currentStep === 'review' && renderReviewStep()}
      </div>
    </div>
  );
};

export default DataSourceWizard;
